import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Zap, Rocket, HeartHandshake, TrendingUp, Award } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Reveal from "@/components/site/Reveal";
import Marquee from "@/components/site/Marquee";
import Counter from "@/components/site/Counter";
import SectionHeading from "@/components/site/SectionHeading";
import FaqAccordion from "@/components/site/FaqAccordion";
import Hero from "@/components/home/Hero";
import { ServiceCard, PortfolioCard, TestimonialCard, BlogCard, PricingCard } from "@/components/site/cards";
import { stagger, fadeUp, viewportOnce } from "@/lib/motion";

const TRUSTED = ["Nova Capital", "Aurora Living", "Pulse Health", "Quantum Labs", "Meridian Group", "Atlas Industries", "Vertex", "Lumen"];

const STATS = [
  { value: 250, suffix: "+", label: "Projects delivered" },
  { value: 98, suffix: "%", label: "Client retention" },
  { value: 12, suffix: "", label: "Countries served" },
  { value: 47, suffix: "%", label: "Avg. conversion lift" },
];

const WHY = [
  { icon: Zap, title: "Blazing performance", desc: "We ship sites that hit 100 on desktop PageSpeed and 95+ on mobile — speed is a feature." },
  { icon: Award, title: "Award-worthy craft", desc: "Every pixel, interaction and line of code is treated like it belongs in a gallery." },
  { icon: ShieldCheck, title: "Enterprise security", desc: "CSRF, XSS and injection protection, secure auth and hardened infrastructure by default." },
  { icon: TrendingUp, title: "Growth engineered", desc: "SEO, CRO and analytics baked into everything so your investment compounds." },
  { icon: Rocket, title: "Ship faster", desc: "Battle-tested processes and reusable systems get you to market without cutting corners." },
  { icon: HeartHandshake, title: "True partnership", desc: "We embed with your team and stay long after launch with proactive support." },
];

const PROCESS = [
  { n: "01", title: "Discover", desc: "We immerse in your business, users and goals to define a strategy that wins." },
  { n: "02", title: "Design", desc: "We craft distinctive, conversion-focused experiences validated by research." },
  { n: "03", title: "Build", desc: "Our engineers ship fast, secure and scalable products with obsessive quality." },
  { n: "04", title: "Grow", desc: "We optimise, measure and scale so your product keeps compounding results." },
];

export default function Home() {
  const [data, setData] = useState({ services: [], portfolio: [], testimonials: [], pricing: [], faqs: [], blog: [] });

  useEffect(() => {
    Promise.all([
      api.get("/services"),
      api.get("/portfolio", { params: { featured: true } }),
      api.get("/testimonials"),
      api.get("/pricing"),
      api.get("/faqs"),
      api.get("/blog", { params: { limit: 3 } }),
    ])
      .then(([s, p, t, pr, f, b]) =>
        setData({ services: s.data, portfolio: p.data, testimonials: t.data, pricing: pr.data, faqs: f.data, blog: b.data })
      )
      .catch(() => {});
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Web Vantage Enterprises",
    description: "Premium digital agency for websites, apps, AI automation and SEO.",
    url: "https://webvantagenterprises.com",
  };

  return (
    <>
      <SEO
        title="Premium Digital Agency"
        description="Web Vantage Enterprises builds award-winning websites, custom apps, AI automation and SEO that drive measurable growth for ambitious brands."
        path="/"
        schema={schema}
      />
      <Hero />

      {/* Trusted by */}
      <section className="border-y border-white/10 py-12" data-testid="trusted-by">
        <div className="container-wv">
          <p className="mb-8 text-center text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
            Trusted by ambitious teams worldwide
          </p>
          <Marquee>
            {TRUSTED.map((name, i) => (
              <span key={i} className="font-heading text-2xl font-semibold text-slate-500 transition-colors hover:text-white">
                {name}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 lg:py-28" data-testid="stats">
        <div className="container-wv">
          <motion.div
            variants={stagger(0.12)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="grid grid-cols-2 gap-8 lg:grid-cols-4"
          >
            {STATS.map((s) => (
              <motion.div key={s.label} variants={fadeUp} className="border-l border-white/10 pl-6">
                <p className="font-heading text-4xl font-bold text-white sm:text-5xl">
                  <Counter to={s.value} suffix={s.suffix} />
                </p>
                <p className="mt-2 text-sm text-slate-400">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28" data-testid="services-section">
        <div className="container-wv">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHeading
              overline="What we do"
              title="Full-stack capabilities, one partner"
              subtitle="From strategy to launch and beyond — everything you need to build and grow a world-class digital presence."
            />
            <Reveal>
              <Link to="/services" className="btn-ghost shrink-0" data-testid="services-view-all">
                All services <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <motion.div
            variants={stagger(0.08)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {data.services.slice(0, 6).map((s, i) => (
              <motion.div key={s.id} variants={fadeUp}>
                <ServiceCard service={s} index={i} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why choose us — bento */}
      <section className="py-20 lg:py-28" data-testid="why-us">
        <div className="container-wv">
          <SectionHeading overline="Why Web Vantage" title="The unfair advantage your competitors wish they had" />
          <motion.div
            variants={stagger(0.08)}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {WHY.map((w) => (
              <motion.div
                key={w.title}
                variants={fadeUp}
                className="group rounded-3xl border border-white/10 bg-white/[0.03] p-8 transition-colors duration-300 hover:border-vantage/40"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-vantage/10 text-vantage">
                  <w.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-white">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{w.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Case studies / portfolio */}
      <section className="py-20 lg:py-28" data-testid="case-studies">
        <div className="container-wv">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHeading overline="Selected work" title="Outcomes we're proud of" />
            <Reveal>
              <Link to="/portfolio" className="btn-ghost shrink-0">
                Full portfolio <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {data.portfolio.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <PortfolioCard item={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process timeline — numbered manifesto */}
      <section className="py-20 lg:py-28" data-testid="process">
        <div className="container-wv">
          <SectionHeading overline="How we work" title="A process built to deliver, chapter by chapter" />
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 md:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1} className="group bg-white/[0.02] p-8 transition-colors duration-300 hover:bg-vantage/[0.06]">
                <span className="font-mono text-sm text-vantage">{p.n}</span>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-white">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28" data-testid="testimonials-section">
        <div className="container-wv">
          <SectionHeading overline="Client love" title="Trusted by leaders who demand the best" align="center" className="mb-14" />
          <div className="grid gap-6 md:grid-cols-2">
            {data.testimonials.slice(0, 4).map((t, i) => (
              <Reveal key={t.id} delay={i * 0.06}>
                <TestimonialCard t={t} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="py-20 lg:py-28" data-testid="pricing-preview">
        <div className="container-wv">
          <SectionHeading overline="Pricing" title="Transparent investment, exceptional return" align="center" className="mb-14" />
          <div className="grid gap-6 lg:grid-cols-3">
            {data.pricing.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <PricingCard plan={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28" data-testid="faq-section">
        <div className="container-wv grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading
            overline="FAQ"
            title="Answers to the questions you're asking"
            subtitle="Still curious? Reach out and we'll get you the clarity you need."
          />
          <Reveal>
            <FaqAccordion items={data.faqs.slice(0, 5)} />
          </Reveal>
        </div>
      </section>

      {/* Latest blog */}
      <section className="py-20 lg:py-28" data-testid="latest-blog">
        <div className="container-wv">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <SectionHeading overline="Insights" title="From the Vantage journal" />
            <Reveal>
              <Link to="/blog" className="btn-ghost shrink-0">All articles <ArrowUpRight className="h-4 w-4" /></Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {data.blog.map((post, i) => (
              <Reveal key={post.id} delay={i * 0.08}>
                <BlogCard post={post} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
}

export function ContactCTA() {
  return (
    <section className="py-20 lg:py-28" data-testid="contact-cta">
      <div className="container-wv">
        <Reveal className="relative overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-ink-900 to-ink-950 p-10 text-center lg:p-20">
          <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-[600px] -translate-x-1/2 rounded-full bg-vantage/20 blur-[120px]" />
          <div className="relative">
            <h2 className="mx-auto max-w-3xl font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Let's build something the industry talks about.
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base text-slate-400 sm:text-lg">
              Tell us about your vision. We'll respond within 24 hours with a plan to make it real.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-primary group" data-testid="cta-start">
                Start your project
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/pricing" className="btn-ghost">See pricing</Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
